import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';
import {
  BroadcastBulkPayload,
  BroadcastBulkResult,
  BroadcastSendPayload,
  BroadcastSendResult,
  CreateBanPayload,
  EditMessageFlagsPayload,
  FlagReportPayload,
  MessagingBan,
  MessageReport,
  ReportResolveAction,
  SetBlockPayload,
} from '@/client/types/messaging';
import { messagingKeys } from './messaging.query';

const B = '/api/admin/v2';
const TIMEOUT = { timeout: 45_000 };

/** ff-api wraps every payload in `{ status, data }` — always read `.data`. */
const unwrap = async <T>(promise: Promise<APIResponse<T> | T>): Promise<T> => {
  const res = await promise;
  if (res != null && typeof res === 'object' && 'data' in (res as object) && 'status' in (res as object)) {
    return (res as APIResponse<T>).data as T;
  }
  return res as T;
};

const invalidateMessages = (qc: ReturnType<typeof useQueryClient>, ref?: string) => {
  qc.invalidateQueries({ queryKey: messagingKeys.messages() });
  qc.invalidateQueries({ queryKey: messagingKeys.overview() });
  if (ref) qc.invalidateQueries({ queryKey: messagingKeys.messageDetail(ref) });
};

const invalidateConversations = (qc: ReturnType<typeof useQueryClient>, key?: string) => {
  qc.invalidateQueries({ queryKey: messagingKeys.conversations() });
  qc.invalidateQueries({ queryKey: messagingKeys.overview() });
  if (key) {
    qc.invalidateQueries({ queryKey: messagingKeys.conversationDetail(key) });
    qc.invalidateQueries({ queryKey: messagingKeys.conversations() });
  }
};

const invalidateReports = (qc: ReturnType<typeof useQueryClient>, rid?: string) => {
  qc.invalidateQueries({ queryKey: messagingKeys.reports() });
  qc.invalidateQueries({ queryKey: messagingKeys.overview() });
  if (rid) qc.invalidateQueries({ queryKey: messagingKeys.reportDetail(rid) });
};

const invalidateBans = (qc: ReturnType<typeof useQueryClient>, banid?: string) => {
  qc.invalidateQueries({ queryKey: messagingKeys.bans() });
  qc.invalidateQueries({ queryKey: messagingKeys.overview() });
  if (banid) qc.invalidateQueries({ queryKey: messagingKeys.banDetail(banid) });
};

// ─── Messages ─────────────────────────────────────────────────────────────────

export const useEditMessageFlagsMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { ref: string; patch: EditMessageFlagsPayload; idempotencyKey?: string }) => {
      return unwrap(
        request(
          `${B}/messages/${encodeURIComponent(args.ref)}`,
          'PATCH',
          args.patch,
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
    },
    'Failed to edit message flags',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { ref }) => invalidateMessages(qc, ref),
  });
};

export const useSoftDeleteMessageMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { ref: string; reason?: string; idempotencyKey?: string }) => {
      return unwrap(
        request(
          `${B}/messages/${encodeURIComponent(args.ref)}/delete`,
          'POST',
          { reason: args.reason },
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
    },
    'Failed to soft-delete message',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { ref }) => {
      invalidateMessages(qc, ref);
      qc.invalidateQueries({ queryKey: messagingKeys.conversations() });
    },
  });
};

export const useRestoreMessageMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { ref: string; idempotencyKey?: string }) => {
      return unwrap(
        request(
          `${B}/messages/${encodeURIComponent(args.ref)}/restore`,
          'POST',
          {},
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
    },
    'Failed to restore message',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { ref }) => {
      invalidateMessages(qc, ref);
      qc.invalidateQueries({ queryKey: messagingKeys.conversations() });
    },
  });
};

export const useRedactMessageMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { ref: string; reason?: string; idempotencyKey?: string }) => {
      return unwrap(
        request(
          `${B}/messages/${encodeURIComponent(args.ref)}/redact`,
          'POST',
          { reason: args.reason },
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
    },
    'Failed to redact message',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { ref }) => invalidateMessages(qc, ref),
  });
};

export const usePurgeMessageMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { ref: string; reason?: string; idempotencyKey?: string }) => {
      return unwrap(
        request(
          `${B}/messages/${encodeURIComponent(args.ref)}/purge`,
          'POST',
          { reason: args.reason },
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
    },
    'Failed to purge message',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { ref }) => {
      invalidateMessages(qc, ref);
      qc.invalidateQueries({ queryKey: messagingKeys.conversations() });
    },
  });
};

export const useSetBlockMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { body: SetBlockPayload; idempotencyKey?: string }) => {
      return unwrap(
        request(`${B}/messages/blocks`, 'POST', args.body, {
          ...TIMEOUT,
          idempotencyKey: args.idempotencyKey || generateIdempotencyKey(),
        }),
      );
    },
    'Failed to update block',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { body }) => {
      qc.invalidateQueries({ queryKey: messagingKeys.blocks(body.user_uid) });
    },
  });
};

// ─── Conversations ────────────────────────────────────────────────────────────

export const usePurgeConversationMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { key: string; reason?: string; idempotencyKey?: string }) => {
      return unwrap(
        request(
          `${B}/message-conversations/${encodeURIComponent(args.key)}/purge`,
          'POST',
          { reason: args.reason },
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
    },
    'Failed to soft-purge conversation',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { key }) => {
      invalidateConversations(qc, key);
      invalidateMessages(qc);
    },
  });
};

/** Policy block: nobody in the thread can send until it is unblocked. */
export const useSetConversationBlockMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { key: string; blocked: boolean; reason: string }) => {
      return unwrap(
        request(
          `${B}/message-conversations/${encodeURIComponent(args.key)}/${args.blocked ? 'block' : 'unblock'}`,
          'POST',
          { reason: args.reason },
          { ...TIMEOUT, idempotencyKey: generateIdempotencyKey() },
        ),
      );
    },
    'Failed to update conversation block',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { key }) => invalidateConversations(qc, key),
  });
};

export const usePurgeConversationHardMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { key: string; reason?: string; idempotencyKey?: string }) => {
      return unwrap(
        request(
          `${B}/message-conversations/${encodeURIComponent(args.key)}/purge-hard`,
          'POST',
          { reason: args.reason },
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
    },
    'Failed to hard-purge conversation',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { key }) => {
      invalidateConversations(qc, key);
      invalidateMessages(qc);
    },
  });
};

// ─── Reports ──────────────────────────────────────────────────────────────────

export const useFlagReportMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { body: FlagReportPayload; idempotencyKey?: string }) => {
      return unwrap(
        request<APIResponse<MessageReport>>(`${B}/message-reports`, 'POST', args.body, {
          ...TIMEOUT,
          idempotencyKey: args.idempotencyKey || generateIdempotencyKey(),
        }),
      );
    },
    'Failed to flag message',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: () => invalidateReports(qc),
  });
};

export const useResolveReportMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: {
      rid: string;
      action: ReportResolveAction;
      notes?: string;
      idempotencyKey?: string;
    }) => {
      return unwrap(
        request(
          `${B}/message-reports/${args.rid}/${args.action}`,
          'POST',
          { notes: args.notes },
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
    },
    'Failed to resolve report',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { rid }) => {
      invalidateReports(qc, rid);
      invalidateMessages(qc);
    },
  });
};

// ─── Broadcast ────────────────────────────────────────────────────────────────

export const useBroadcastSendMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { body: BroadcastSendPayload; idempotencyKey?: string }) => {
      return unwrap(
        request<APIResponse<BroadcastSendResult>>(`${B}/message-broadcast/send`, 'POST', args.body, {
          ...TIMEOUT,
          idempotencyKey: args.idempotencyKey || generateIdempotencyKey(),
        }),
      );
    },
    'Failed to send platform message',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      invalidateMessages(qc);
      invalidateConversations(qc);
    },
  });
};

export const useBroadcastBulkMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { body: BroadcastBulkPayload; idempotencyKey?: string }) => {
      return unwrap(
        request<APIResponse<BroadcastBulkResult>>(`${B}/message-broadcast/bulk`, 'POST', args.body, {
          ...TIMEOUT,
          idempotencyKey: args.idempotencyKey || generateIdempotencyKey(),
        }),
      );
    },
    'Failed to broadcast messages',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      invalidateMessages(qc);
      invalidateConversations(qc);
    },
  });
};

// ─── Bans ─────────────────────────────────────────────────────────────────────

export const useCreateBanMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { body: CreateBanPayload; idempotencyKey?: string }) => {
      return unwrap(
        request<APIResponse<{ success?: boolean; ban?: MessagingBan }>>(
          `${B}/message-bans`,
          'POST',
          args.body,
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
    },
    'Failed to create messaging ban',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: () => invalidateBans(qc),
  });
};

export const useLiftBanMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { banid: string; idempotencyKey?: string }) => {
      return unwrap(
        request(
          `${B}/message-bans/${args.banid}/lift`,
          'POST',
          {},
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
    },
    'Failed to lift ban',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { banid }) => invalidateBans(qc, banid),
  });
};
